import React from 'react';
import { Link } from 'react-router-dom';
import { Radar, Shield, Target, AlertTriangle, Crosshair, Globe, Activity, Bug, Users, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { sleep } from '../../utils/sleep';
import { Badge } from '../../components/ui/Badge';

const ThreatDashboard = () => {
  const { isLoading, withLoading } = useLoading();

  const handleRefresh = async () => {
    await withLoading(async () => {
      await sleep(3000);
      console.log('Refreshing threat data...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Refreshing threat data..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Radar className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Threat Intelligence</h1>
            <p className="text-sm text-gray-400">Monitor and analyze security threats</p>
          </div>
        </div>
        <Button onClick={handleRefresh}>
          <Activity className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Threat Overview */}
      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Active Threats', value: '24', trend: '+3', icon: AlertTriangle, color: 'text-red-500' },
          { label: 'IOCs Detected', value: '156', trend: '+12', icon: Target, color: 'text-purple-500' },
          { label: 'Threat Feeds', value: '12', trend: '0', icon: Shield, color: 'text-emerald-500' },
          { label: 'Global Coverage', value: '89%', trend: '+2%', icon: Globe, color: 'text-blue-500' }
        ].map((metric) => (
          <div key={metric.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">{metric.label}</span>
              <metric.icon className={`h-5 w-5 ${metric.color}`} />
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-white">{metric.value}</div>
              <div className={`text-xs ${metric.trend.startsWith('+') ? 'text-emerald-500' : 'text-gray-500'}`}>
                {metric.trend}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent News and Activity */}
      <div className="grid grid-cols-2 gap-6">
        {/* Recent News */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Recent Threat News</h2>
            <Link to="/threat-intelligence/news">
              <Button variant="outline" size="sm">View All News</Button>
            </Link>
          </div>
          <div className="space-y-4">
            {[
              {
                title: 'Critical Zero-Day Vulnerability in Popular Software',
                source: 'CyberNews',
                time: '2h ago',
                image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=400&h=300&fit=crop'
              },
              {
                title: 'New Ransomware Campaign Targeting Healthcare Sector',
                source: 'ThreatPost',
                time: '4h ago',
                image: 'https://images.unsplash.com/photo-1576444356170-66073046b1bc?w=400&h=300&fit=crop'
              },
              {
                title: 'APT Group Expands Operations to New Regions',
                source: 'SecurityWeek',
                time: '6h ago',
                image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=300&fit=crop'
              },
              {
                title: 'Major Data Breach Affects Millions of Users',
                source: 'BleepingComputer',
                time: '8h ago',
                image: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=400&h=300&fit=crop'
              }
            ].map((news, index) => (
              <div key={index} className="p-4 bg-gray-800/30 rounded-lg hover:bg-gray-800/40 transition-colors cursor-pointer">
                <div className="flex space-x-4">
                  <div className="flex-shrink-0">
                    <img 
                      src={news.image} 
                      alt={news.title}
                      className="w-20 h-16 rounded-lg object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-white">{news.title}</div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-400">{news.source}</span>
                      <span className="text-xs text-gray-400">{news.time}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent CVEs */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Recent CVEs</h2>
            <Link to="/threat-intelligence/cve">
              <Button variant="outline" size="sm">View All</Button>
            </Link>
          </div>
          <div className="space-y-4">
            {[
              {
                id: 'CVE-2024-1234',
                title: 'Remote Code Execution in Popular Framework',
                severity: 'critical',
                cvss: '9.8',
                status: 'new'
              },
              {
                id: 'CVE-2024-5678',
                title: 'SQL Injection Vulnerability in CMS',
                severity: 'high',
                cvss: '8.5',
                status: 'analyzing'
              },
              {
                id: 'CVE-2024-9012',
                title: 'Cross-Site Scripting in E-commerce Platform',
                severity: 'medium',
                cvss: '6.5',
                status: 'patched'
              }
            ].map((cve, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg hover:bg-gray-800/40 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="flex flex-col">
                    <div className="text-sm font-mono text-gray-400">{cve.id}</div>
                    <div className="text-sm font-medium text-white mt-1">{cve.title}</div>
                    <div className="flex items-center space-x-3 mt-2">
                      <Badge variant={cve.severity as any}>{cve.severity}</Badge>
                      <Badge variant={cve.status as any}>{cve.status}</Badge>
                      <span className={`text-sm font-medium ${
                        Number(cve.cvss) >= 9.0 ? 'text-red-500' :
                        Number(cve.cvss) >= 7.0 ? 'text-orange-500' :
                        Number(cve.cvss) >= 4.0 ? 'text-yellow-500' :
                        'text-emerald-500'
                      }`}>
                        CVSS {cve.cvss}
                      </span>
                    </div>
                  </div>
                </div>
                <Link to={`/threat-intelligence/cve?id=${cve.id}`}>
                  <Button variant="outline" size="sm">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Threat Categories */}
      <div className="grid grid-cols-3 gap-6">
        {[
          { title: 'Malware Analysis', count: 156, trend: '+12', color: 'text-red-500' },
          { title: 'Phishing Campaigns', count: 89, trend: '-5', color: 'text-yellow-500' },
          { title: 'Advanced Threats', count: 34, trend: '+3', color: 'text-purple-500' }
        ].map((category) => (
          <div key={category.title} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{category.title}</h3>
              <div className={`text-xs ${category.trend.startsWith('+') ? 'text-red-500' : 'text-emerald-500'}`}>
                {category.trend}%
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-4">{category.count}</div>
            <Button variant="outline" className="w-full">View Details</Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThreatDashboard;